import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Download, ArrowDown, Sparkles } from 'lucide-react';
import MagneticButton from './MagneticButton';

const ROLES = ['Full Stack Developer', 'MERN Stack Specialist', "Master's Student"];

const Hero = () => {
  const [displayText, setDisplayText] = useState('');
  const [roleIndex, setRoleIndex] = useState(0);

  useEffect(() => {
    const currentText = ROLES[roleIndex];
    let i = 0;
    const typing = setInterval(() => {
      if (i <= currentText.length) {
        setDisplayText(currentText.slice(0, i));
        i++;
      } else {
        clearInterval(typing);
        setTimeout(() => setRoleIndex((prev) => (prev + 1) % ROLES.length), 1800);
      }
    }, 80);
    return () => clearInterval(typing);
  }, [roleIndex]);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center bg-ink-950 overflow-hidden font-body"
    >
      {/* Aurora / gradient mesh background */}
      <div className="absolute inset-0 bg-aurora" />
      <div className="absolute inset-0 bg-grain" />
      <div className="absolute top-1/3 -left-1/4 w-[50%] h-[50%] bg-mint-500/10 rounded-full blur-3xl animate-drift" />
      <div
        className="absolute bottom-0 -right-1/4 w-[55%] h-[55%] bg-indigo-500/10 rounded-full blur-3xl animate-drift"
        style={{ animationDelay: '4s' }}
      />

      <div className="relative w-full px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full glass text-xs sm:text-sm text-white/60 font-mono"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mint-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-mint-400" />
            </span>
            Open to full-stack opportunities
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1]"
          >
            Hi, I'm{' '}
            <span className="text-gradient bg-[length:200%_auto] animate-gradient-x">
              Muhammad Tauqeer
            </span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-5 h-9 sm:h-11 flex items-center justify-center font-mono text-lg sm:text-2xl lg:text-3xl text-mint-400"
          >
            {displayText}
            <span className="w-[2px] h-6 sm:h-7 bg-mint-400 ml-1 animate-blink" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-6 text-base sm:text-lg text-white/50 max-w-xl mx-auto"
          >
            I design and build end-to-end web products — from pixel-perfect React interfaces
            to the APIs and databases running underneath them.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-4"
          >
            <MagneticButton
              as="button"
              onClick={() => document.getElementById('terminal')?.scrollIntoView({ behavior: 'smooth' })}
              className="interactive inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-mint-400 to-cyan-400 text-ink-950 font-semibold shadow-glow"
            >
              <Sparkles className="w-4 h-4" />
              Play with my terminal
            </MagneticButton>
            <MagneticButton
              href="/Muhammad-Tauqeer-Cv.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="interactive inline-flex items-center gap-2 px-6 py-3 rounded-full glass text-white font-medium hover:border-white/20"
            >
              <Download className="w-4 h-4" />
              Download CV
            </MagneticButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-10 flex items-center justify-center gap-4"
          >
            <a
              href="https://github.com/mtauqeer248"
              target="_blank"
              rel="noopener noreferrer"
              className="interactive p-3 rounded-full glass text-white/70 hover:text-mint-300 hover:border-mint-400/40 transition-colors"
              aria-label="GitHub"
            >
              <Github className="w-5 h-5" />
            </a>
            <a
              href="https://www.linkedin.com/in/muhammad-tauqeer-react-developer/"
              target="_blank"
              rel="noopener noreferrer"
              className="interactive p-3 rounded-full glass text-white/70 hover:text-mint-300 hover:border-mint-400/40 transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-8 inset-x-0 flex justify-center">
        <motion.button
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          className="interactive flex flex-col items-center gap-2 text-white/40 hover:text-white/70 transition-colors"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          <span className="text-xs font-mono tracking-widest uppercase">Scroll</span>
          <ArrowDown className="w-4 h-4" />
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
